import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '@/store/store';
import { transferFunds, fetchBalance } from '@/store/slices/walletSlice';
import { walletApi } from '@/services/api/wallet';
import { FiX, FiSearch, FiUser, FiCheckCircle } from 'react-icons/fi';
import { Currency, UserSearchResult } from '@/types/wallet';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import { useCurrency } from '@/contexts/CurrencyContext';

interface TransferModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const TransferModal: React.FC<TransferModalProps> = ({ isOpen, onClose }) => {
  const dispatch = useDispatch<AppDispatch>();
  const { balance, actionLoading } = useSelector((state: RootState) => state.wallet);
  const { userCurrency } = useCurrency();

  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<UserSearchResult[]>([]);
  const [searching, setSearching] = useState(false);
  const [selectedUser, setSelectedUser] = useState<UserSearchResult | null>(null);
  const [amount, setAmount] = useState('');
  const [currency, setCurrency] = useState<Currency>(userCurrency as Currency);
  const [notes, setNotes] = useState('');

  useEffect(() => {
    if (searchQuery.length >= 3) {
      searchUsers();
    } else {
      setSearchResults([]);
    }
  }, [searchQuery]);

  const searchUsers = async () => {
    try {
      setSearching(true);
      const response = await walletApi.searchUsers(searchQuery);
      setSearchResults(response.data.data);
    } catch (error) {
      console.error('User search failed:', error);
    } finally {
      setSearching(false);
    }
  };

  const handleSelectUser = (user: UserSearchResult) => {
    setSelectedUser(user);
    setStep(2);
  };

  const handleNext = () => {
    if (!amount || parseFloat(amount) <= 0) {
      return;
    }
    setStep(3);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedUser || !amount || parseFloat(amount) <= 0) {
      return;
    }

    const result = await dispatch(
      transferFunds({
        recipientId: selectedUser._id,
        recipientEmail: selectedUser.email,
        amount: parseFloat(amount),
        currency,
        notes,
      })
    );

    if (result.meta.requestStatus === 'fulfilled') {
      onClose();
      dispatch(fetchBalance(currency));
      resetForm();
    }
  };

  const resetForm = () => {
    setStep(1);
    setSearchQuery('');
    setSelectedUser(null);
    setAmount('');
    setNotes('');
  };

  if (!isOpen) return null;

  const availableBalance = balance?.available || 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-3 backdrop-blur-sm sm:p-4">
      <div className="max-h-[90vh] w-full max-w-md overflow-y-auto rounded-2xl border border-slate-200 bg-white shadow-xl dark:border-gray-800 dark:bg-gray-900">
        <div className="flex items-center justify-between border-b border-slate-200 p-4 dark:border-gray-800 sm:p-5">
          <h2 className="text-xl font-bold text-slate-900 dark:text-white sm:text-2xl">
            Transfer Funds
          </h2>
          <button
            onClick={() => {
              onClose();
              resetForm();
            }}
            title="Close transfer modal"
            className="rounded-lg p-1 text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-700 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-gray-200"
          >
            <FiX size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 sm:p-5">
          {step === 1 && (
            <div className="space-y-6">
              <div>
                <label className="mb-2 block text-sm font-medium text-slate-700 dark:text-gray-300">
                  Search Recipient
                </label>
                <div className="relative">
                  <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 dark:text-gray-500" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search by name or email..."
                    className="w-full rounded-xl border border-slate-300 bg-white py-2.5 pl-10 pr-4 text-slate-900 focus:border-transparent focus:ring-2 focus:ring-primary-500 dark:border-gray-700 dark:bg-gray-800 dark:text-white"
                  />
                </div>
              </div>

              {searching && (
                <div className="flex items-center justify-center py-8">
                  <LoadingSpinner size="md" />
                </div>
              )}

              {!searching && searchResults.length > 0 && (
                <div className="space-y-2 max-h-80 overflow-y-auto">
                  {searchResults.map((user) => (
                    <button
                      key={user._id}
                      type="button"
                      onClick={() => handleSelectUser(user)}
                      className="flex w-full items-center gap-3 rounded-xl border border-slate-200 p-3 transition-colors hover:border-primary-500 hover:bg-primary-50 dark:border-gray-800 dark:hover:bg-primary-900/20"
                    >
                      {user.profilePicture ? (
                        <img
                          src={user.profilePicture}
                          alt={`${user.firstName} ${user.lastName}`}
                          className="w-12 h-12 rounded-full object-cover"
                        />
                      ) : (
                        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-slate-200 dark:bg-gray-700">
                          <FiUser className="text-slate-500 dark:text-gray-400" size={24} />
                        </div>
                      )}
                      <div className="flex-1 text-left">
                        <div className="flex items-center gap-2">
                          <p className="font-medium text-slate-900 dark:text-white">
                            {user.firstName} {user.lastName}
                          </p>
                          {user.verified && (
                            <FiCheckCircle className="text-primary-600" size={16} />
                          )}
                        </div>
                        <p className="text-sm text-slate-600 dark:text-gray-400">{user.email}</p>
                      </div>
                    </button>
                  ))}
                </div>
              )}

              {!searching && searchQuery.length >= 3 && searchResults.length === 0 && (
                <div className="text-center py-8">
                  <p className="text-slate-500 dark:text-gray-400">No users found</p>
                </div>
              )}
            </div>
          )}

          {step === 2 && selectedUser && (
            <div className="space-y-6">
              <div>
                <button
                  type="button"
                  onClick={() => setStep(1)}
                  className="text-sm text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 mb-4"
                >
                  ← Change Recipient
                </button>

                <div className="mb-4 rounded-xl border border-slate-200 bg-slate-50 p-4 dark:border-gray-800 dark:bg-gray-900/50">
                  <p className="mb-2 text-sm text-slate-600 dark:text-gray-400">
                    Transferring to:
                  </p>
                  <div className="flex items-center gap-3">
                    {selectedUser.profilePicture ? (
                      <img
                        src={selectedUser.profilePicture}
                        alt={`${selectedUser.firstName} ${selectedUser.lastName}`}
                        className="w-10 h-10 rounded-full object-cover"
                      />
                    ) : (
                      <div className="flex h-10 w-10 items-center justify-center rounded-full bg-slate-200 dark:bg-gray-700">
                        <FiUser className="text-slate-500 dark:text-gray-400" size={20} />
                      </div>
                    )}
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="font-medium text-slate-900 dark:text-white">
                          {selectedUser.firstName} {selectedUser.lastName}
                        </p>
                        {selectedUser.verified && (
                          <FiCheckCircle className="text-primary-600" size={14} />
                        )}
                      </div>
                      <p className="text-sm text-slate-600 dark:text-gray-400">
                        {selectedUser.email}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 dark:border-gray-800 dark:bg-gray-900/50">
                <p className="mb-1 text-sm text-slate-600 dark:text-gray-400">
                  Available Balance
                </p>
                <p className="text-xl font-bold text-slate-900 dark:text-white sm:text-2xl">
                  {availableBalance.toFixed(2)} {currency}
                </p>
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-slate-700 dark:text-gray-300">
                  Transfer Amount
                </label>
                <input
                  type="number"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder="0.00"
                  step="0.01"
                  min="1"
                  max={availableBalance}
                  required
                  className="w-full rounded-xl border border-slate-300 bg-white px-4 py-2.5 text-base text-slate-900 focus:border-transparent focus:ring-2 focus:ring-primary-500 dark:border-gray-700 dark:bg-gray-800 dark:text-white"
                />
                <p className="mt-2 text-sm text-slate-500 dark:text-gray-400">
                  Maximum: {availableBalance.toFixed(2)} {currency}
                </p>
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-slate-700 dark:text-gray-300">
                  Note (Optional)
                </label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Add a note for this transfer..."
                  rows={3}
                  className="w-full resize-none rounded-xl border border-slate-300 bg-white px-4 py-2.5 text-slate-900 focus:border-transparent focus:ring-2 focus:ring-primary-500 dark:border-gray-700 dark:bg-gray-800 dark:text-white"
                />
              </div>

              <button
                type="button"
                onClick={handleNext}
                disabled={!amount || parseFloat(amount) <= 0 || parseFloat(amount) > availableBalance}
                className="w-full rounded-xl bg-primary-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Continue
              </button>
            </div>
          )}

          {step === 3 && selectedUser && (
            <div className="space-y-6">
              <div>
                <button
                  type="button"
                  onClick={() => setStep(2)}
                  className="text-sm text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 mb-4"
                >
                  ← Back
                </button>
              </div>

              <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 dark:border-gray-800 dark:bg-gray-900/50">
                <h4 className="mb-3 font-medium text-slate-900 dark:text-white">
                  Transfer Summary
                </h4>
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-600 dark:text-gray-400">Recipient:</span>
                    <span className="font-medium text-slate-900 dark:text-white">
                      {selectedUser.firstName} {selectedUser.lastName}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600 dark:text-gray-400">Amount:</span>
                    <span className="font-medium text-slate-900 dark:text-white">
                      {parseFloat(amount).toFixed(2)} {currency}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600 dark:text-gray-400">Transfer Fee:</span>
                    <span className="font-medium text-slate-900 dark:text-white">
                      0.00 {currency}
                    </span>
                  </div>
                  {notes && (
                    <div className="border-t border-slate-200 pt-2 dark:border-gray-800">
                      <span className="text-slate-600 dark:text-gray-400">Note:</span>
                      <p className="mt-1 text-slate-900 dark:text-white">{notes}</p>
                    </div>
                  )}
                </div>
              </div>

              <button
                type="submit"
                disabled={actionLoading}
                className="flex w-full items-center justify-center gap-2 rounded-xl bg-primary-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {actionLoading ? (
                  <>
                    <LoadingSpinner size="sm" color="white" />
                    Processing...
                  </>
                ) : (
                  'Confirm Transfer'
                )}
              </button>
            </div>
          )}
        </form>
      </div>
    </div>
  );
};

export default TransferModal;
